import type React from 'react';
import type { InternalColumnDef } from '../schema/types';

export interface PinOffsets {
  left: Map<string, number>;
  right: Map<string, number>;
}

const CHECKBOX_COL_WIDTH = 40;

/**
 * Computes cumulative left/right CSS sticky offsets for pinned columns.
 * Left-pinned columns accumulate from the left edge (after the optional checkbox column).
 * Right-pinned columns accumulate from the right edge (before the optional actions column).
 */
export function computePinOffsets(
  columns: InternalColumnDef[],
  columnSizes: Record<string, number>,
  showCheckbox: boolean,
): PinOffsets {
  const leftPinned = columns.filter((c) => c.pinned === 'left');
  const rightPinned = columns.filter((c) => c.pinned === 'right');

  let leftOffset = showCheckbox ? CHECKBOX_COL_WIDTH : 0;
  const left = new Map<string, number>();
  for (const col of leftPinned) {
    left.set(col.field, leftOffset);
    leftOffset += columnSizes[col.field] ?? col.width.default;
  }

  let rightOffset = 0;
  const right = new Map<string, number>();
  for (let i = rightPinned.length - 1; i >= 0; i--) {
    const col = rightPinned[i];
    right.set(col.field, rightOffset);
    rightOffset += columnSizes[col.field] ?? col.width.default;
  }

  return { left, right };
}

/**
 * Returns inline styles for a pinned cell (works for both th and td).
 * Returns empty object for unpinned columns.
 */
export function getPinnedCellStyle(
  col: InternalColumnDef,
  pinOffsets: PinOffsets,
  isHeader: boolean,
  rowBg?: string,
): React.CSSProperties {
  const bg = rowBg ?? '#ffffff';
  if (col.pinned === 'left') {
    return {
      position: 'sticky',
      left: pinOffsets.left.get(col.field) ?? 0,
      zIndex: isHeader ? 3 : 1,
      backgroundColor: bg,
    };
  }
  if (col.pinned === 'right') {
    return {
      position: 'sticky',
      right: pinOffsets.right.get(col.field) ?? 0,
      zIndex: isHeader ? 3 : 1,
      backgroundColor: bg,
    };
  }
  return {};
}

/**
 * Determines whether a column is the boundary column for box-shadow rendering.
 * Returns the shadow style string or null.
 */
export function getPinnedBoundaryShadow(
  col: InternalColumnDef,
  columns: InternalColumnDef[],
): string | null {
  if (col.pinned === 'left') {
    const leftPinned = columns.filter((c) => c.pinned === 'left');
    if (leftPinned[leftPinned.length - 1]?.field === col.field) {
      return '4px 0 4px -4px rgba(0,0,0,0.1)';
    }
  }
  if (col.pinned === 'right') {
    const rightPinned = columns.filter((c) => c.pinned === 'right');
    if (rightPinned[0]?.field === col.field) {
      return '-4px 0 4px -4px rgba(0,0,0,0.1)';
    }
  }
  return null;
}
